import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = 'surveyTemplate/:id';

export interface ISurveyTemplateQueryParams {
	id: string;
	enabled?: boolean;
}

/**
 * Get a specific survey template by ID.
 * Returns the full template including survey definition.
 *
 * @param params - Query parameters including template ID and optional impersonation context
 * @returns React Query result with full survey template
 */
export function useSurveyTemplateQuery(params: ISurveyTemplateQueryParams) {
	const { id, enabled = true } = params;
	const userSession = useUserSession();

	return useQuery({
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.ISurveyTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `surveytemplate/${id}`,
					queryParams: {
						expand: 'options',
					},
				}),
				'GET'
			);
		},
		queryKey: [BASE_API_KEY, 'detail', id],
		enabled: enabled && !!id,
		staleTime: 10 * 60 * 1000, // 10 minutes
	});
}

export const invalidateSurveyTemplateQuery = (id: string) => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY, 'detail', id] });
};
